import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { AuthSecurityService } from '../auth/auth-security/auth-security.service';
import { LOCAL_STORAGE_KEYS } from '../local-storage/local-storage.config';


@Injectable({ providedIn: 'root' })
export class UpdateAnnouncementDisplayService {

	//** Fallback when browser storage is unavailable */
	private readonly displaysThisLoad: Map<string, Map<string, number>> = new Map<string, Map<string, number>>();

	//** Announcement Events */
	private readonly openRequestSubject: Subject<void> = new Subject<void>();
	readonly openRequests$: Observable<void> = this.openRequestSubject.asObservable();


	constructor(
		@Inject(DOCUMENT) private readonly document: Document,
		private readonly authSecurityService: AuthSecurityService
	) {}


	/**------------------------------------------------------
	 * Open Announcement
	 */
	open(): void {
		this.openRequestSubject.next();
	}


	/**------------------------------------------------------
	 * Display Limits Per User and Update
	 */
	getUserId(): string {
		return this.authSecurityService.getId();
	}

	show(userId: string, updateIds: string[], automatic: boolean, open: () => boolean, displayLimit: number | null = 1, whenClosed?: () => Promise<unknown>): Promise<boolean> {
		const key: string = `${LOCAL_STORAGE_KEYS.updateAnnouncementHistoryPrefix}${encodeURIComponent(userId)}`;
		const hasBudget: (counts: Map<string, number>) => boolean = (counts) => displayLimit === null || (displayLimit > 0 && updateIds.some((id: string) => (counts.get(id) || 0) < displayLimit));
		if (automatic && !hasBudget(this.readDisplayCounts(key))) return Promise.resolve(false);
		const present: () => boolean = () => {
			// Recheck after acquiring the lock: the account or another tab's history may have changed.
			if (!userId || this.getUserId() !== userId || !updateIds.length) return false;
			const counts: Map<string, number> = this.readDisplayCounts(key);
			if (automatic && !hasBudget(counts)) return false;
			if (!open()) return false;

			// Opening presents the entire batch; carousel navigation and dismissal do not count.
			for (const id of new Set(updateIds)) counts.set(id, Math.min((counts.get(id) || 0) + 1, Number.MAX_SAFE_INTEGER));
			this.displaysThisLoad.set(key, counts);
			try {
				this.document.defaultView?.localStorage.setItem(key, JSON.stringify(Object.fromEntries(counts)));
			} catch {
				// Retain history for this app load if persistent browser storage is blocked or full.
			}
			return true;
		};

		const locks: LockManager | undefined = this.document.defaultView?.navigator.locks;
		if (!locks) return Promise.resolve().then(present);
		if (!whenClosed) return locks.request(key, present);

		// Keep an active-modal lock until dismissal, including when the limit exceeds one.
		// Resolve the caller as soon as opening finishes; other tabs skip an already active modal.
		return new Promise<boolean>((resolve, reject) => {
			locks.request(`${key}:active`, { ifAvailable: true }, async (lock: Lock | null) => {
				if (!lock) {
					resolve(false);
					return;
				}
				const opened: boolean = await locks.request(key, present);
				const closed: Promise<unknown> = opened ? whenClosed() : Promise.resolve();
				resolve(opened);
				await closed.catch(() => { /* Dismissal also releases the active-modal lock. */ });
			}).catch(reject);
		});
	}

	private readDisplayCounts(key: string): Map<string, number> {
		const counts: Map<string, number> = new Map(this.displaysThisLoad.get(key));
		try {
			const stored: unknown = JSON.parse(this.document.defaultView?.localStorage.getItem(key) || '{}');
			// Existing arrays mean each ID was presented once; retain that history during migration.
			if (Array.isArray(stored)) {
				for (const id of stored) {
					if (typeof id === 'string') counts.set(id, Math.max(counts.get(id) || 0, 1));
				}
			} else if (stored && typeof stored === 'object') {
				for (const [id, count] of Object.entries(stored)) {
					if (typeof count === 'number' && Number.isSafeInteger(count) && count > 0) counts.set(id, Math.max(counts.get(id) || 0, count));
				}
			}
		} catch {
			// Malformed or unavailable storage does not prevent opening valid announcements.
		}
		return counts;
	}
}
